// Loads station configs from the `stations` table via the NestJS API.
// All self-service surfaces (browse, channel landing, wizard, my campaigns,
// publisher admin) read configs through these hooks so newly-onboarded
// channels show up immediately.
//
// Caching strategy: per-slug and all-configs results are memoized at module
// scope so cross-page navigation (Browse → MyCampaigns → Browse) doesn't
// re-fetch. In-flight promises are deduped so concurrent callers share a
// single network round trip. Cache TTL is short (~30s) — we'd rather pay a
// fresh fetch than show a stale logo after an admin onboards a channel.

import { useEffect, useRef, useState } from 'react';
import { ApiError, apiClient } from '../lib/apiClient';
import type {
  DisplayAdStill,
  MaterialsVideo,
  PublisherConfig,
  PublisherMarket,
  ProductOption,
  ServiceFeeRates,
} from '../types/selfService';
import { DEFAULT_SERVICE_FEE_RATES } from '../types/selfService';

// The NestJS API returns the station shape in camelCase already, so a
// row→config projection is mostly a pass-through with a couple of defaults.
interface StationApiRow {
  slug: string;
  name: string;
  organizationId: string | null;
  displayName?: string | null;
  logoUrl: string | null;
  logoInvert: boolean;
  logoScale?: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  fontUrl: string | null;
  coverage: 'regional' | 'national';
  markets: PublisherMarket[];
  // CTV-674: markets near the station's coverage DMAs; null = no restriction.
  allowedDmaCodes?: string[] | null;
  products: ProductOption[];
  revSharePercent: number;
  // CTV-518: buyer-facing service-fee gross-up rates, from API env config.
  serviceFee?: ServiceFeeRates;
  minBudget: number;
  minCampaignDays: number;
  homeMarketCenter?: { lat: number; lng: number; label: string } | null;
  supportEmail: string | null;
  adopsEmail?: string | null;
  adopsPhone?: string | null;
  defaultCommissionPercent?: number | null;
  termsUrl: string;
  // CTV-610: rendered landing-page background video (null → gradient fallback).
  backgroundVideoUrl?: string | null;
  // CTV-752: shows/hides the publisher admin's Materials tab.
  materialsEnabled?: boolean;
  // CTV-803: devices the station streams on.
  devices?: string[];
  // CTV-840: saved TV-screen stills for the display ads.
  displayAdStills?: DisplayAdStill[];
  // CTV-863: the station's commercials for the Materials tab.
  materialsVideos?: MaterialsVideo[];
}

// Helper: returns the label to render in consumer-facing UI. Use this any
// time the caller wants the "publicly visible" name (browse cards, wizard
// headers, report text, etc.). PublisherConfig.name itself is always the
// raw internal name (= stations.name) so canAdminStation and the admin
// edit form can read the canonical value directly.
export function displayedName(c: { name: string; displayName?: string | null }): string {
  return c.displayName ?? c.name;
}

function rowToConfig(row: StationApiRow): PublisherConfig {
  return {
    slug: row.slug,
    // Raw internal name from stations.name. Used by canAdminStation to
    // match JWT app_metadata.stations[] entries, and shown as-is in the
    // admin form's "Internal name" input. Consumer surfaces that want the
    // displayed label should call displayedName(config) instead.
    name: row.name,
    // Raw display-name column (nullable). When null, displayedName() falls
    // back to the internal name above.
    displayName: row.displayName ?? null,
    organizationId: row.organizationId ?? null,
    logoUrl: row.logoUrl ?? '',
    logoInvert: row.logoInvert,
    logoScale: row.logoScale ?? 1,
    primaryColor: row.primaryColor ?? '',
    secondaryColor: row.secondaryColor ?? '',
    accentColor: row.accentColor ?? '',
    fontFamily: row.fontFamily ?? '',
    fontUrl: row.fontUrl ?? undefined,
    coverage: row.coverage,
    markets: row.markets ?? [],
    allowedDmaCodes: row.allowedDmaCodes ?? null,
    products: row.products ?? [],
    revSharePercent: row.revSharePercent,
    serviceFeeRates: row.serviceFee ?? DEFAULT_SERVICE_FEE_RATES,
    minBudget: row.minBudget,
    minCampaignDays: row.minCampaignDays,
    homeMarketCenter: row.homeMarketCenter ?? null,
    supportEmail: row.supportEmail ?? '',
    adopsEmail: row.adopsEmail ?? '',
    adopsPhone: row.adopsPhone ?? '',
    defaultCommissionPercent: row.defaultCommissionPercent ?? null,
    teamCommissionEnabled: row.defaultCommissionPercent != null,
    termsUrl: row.termsUrl,
    backgroundVideoUrl: row.backgroundVideoUrl ?? null,
    // Default on: an API payload predating the column keeps the tab visible,
    // matching the DB default.
    materialsEnabled: row.materialsEnabled ?? true,
    // CTV-803: devices the station streams on.
    devices: row.devices ?? [],
    // CTV-840: saved TV-screen stills for the display ads.
    displayAdStills: row.displayAdStills ?? [],
    // CTV-863: the station's commercials for the Materials tab.
    materialsVideos: row.materialsVideos ?? [],
  };
}

const CACHE_TTL_MS = 30_000;

interface CacheEntry<T> {
  value: T;
  fetchedAt: number;
}

const slugCache = new Map<string, CacheEntry<PublisherConfig | null>>();
const slugInFlight = new Map<string, Promise<PublisherConfig | null>>();
let allCache: CacheEntry<PublisherConfig[]> | null = null;
let allInFlight: Promise<PublisherConfig[]> | null = null;

// Mounted hooks subscribe here so they can refetch after the cache is
// invalidated. Without this, a stale `config` held in usePublisherConfig's
// useState survives until the slug changes — which means the publisher
// edit form repopulates with pre-save values after Settings → Overview →
// Settings navigation.
let cacheVersion = 0;
const versionListeners = new Set<(v: number) => void>();

function isFresh<T>(entry: CacheEntry<T> | null | undefined): entry is CacheEntry<T> {
  return Boolean(entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS);
}

export function invalidatePublisherCache() {
  slugCache.clear();
  slugInFlight.clear();
  allCache = null;
  allInFlight = null;
  cacheVersion += 1;
  versionListeners.forEach((fn) => fn(cacheVersion));
}

export async function fetchPublisherConfig(slug: string): Promise<PublisherConfig | null> {
  const cached = slugCache.get(slug);
  if (isFresh(cached)) return cached.value;
  const inFlight = slugInFlight.get(slug);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<PublisherConfig | null> => {
    try {
      const data = await apiClient.get<StationApiRow>(`/stations/${encodeURIComponent(slug)}`);
      return rowToConfig(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      console.error('fetchPublisherConfig failed:', e);
      return null;
    }
  })();

  slugInFlight.set(slug, promise);
  try {
    const value = await promise;
    slugCache.set(slug, { value, fetchedAt: Date.now() });
    return value;
  } finally {
    slugInFlight.delete(slug);
  }
}

export async function fetchAllPublisherConfigs(): Promise<PublisherConfig[]> {
  if (isFresh(allCache)) return allCache.value;
  if (allInFlight) return allInFlight;

  allInFlight = (async (): Promise<PublisherConfig[]> => {
    try {
      const data = await apiClient.get<StationApiRow[]>('/stations');
      return data.map(rowToConfig);
    } catch (e) {
      console.error('fetchAllPublisherConfigs failed:', e);
      return [];
    }
  })();

  try {
    const value = await allInFlight;
    allCache = { value, fetchedAt: Date.now() };
    // Warm the per-slug cache too so subsequent usePublisherConfig calls
    // resolve synchronously without re-fetching.
    const at = Date.now();
    for (const c of value) slugCache.set(c.slug, { value: c, fetchedAt: at });
    return value;
  } finally {
    allInFlight = null;
  }
}

export function usePublisherConfig(slug: string | undefined) {
  // Seed synchronously from cache so we don't flash a loading state when
  // the value was already fetched on a previous page.
  const initial = slug ? slugCache.get(slug) : undefined;
  const [config, setConfig] = useState<PublisherConfig | null>(isFresh(initial) ? initial.value : null);
  const [loading, setLoading] = useState<boolean>(Boolean(slug) && !isFresh(initial));
  // Re-runs the fetch effect whenever invalidatePublisherCache bumps the
  // version — so a successful save in PublisherForm immediately refreshes
  // the live config without waiting for slug to change.
  const [version, setVersion] = useState(cacheVersion);
  useEffect(() => {
    versionListeners.add(setVersion);
    return () => { versionListeners.delete(setVersion); };
  }, []);
  // Ref (not the state value) so the fetch effect can decide about the
  // loading flash without re-running whenever config lands.
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    if (!slug) { setConfig(null); setLoading(false); return; }
    const cached = slugCache.get(slug);
    if (isFresh(cached)) { setConfig(cached.value); setLoading(false); return; }
    let cancelled = false;
    // Stale-while-revalidate: a version bump (the post-save invalidation)
    // refetches behind the value we already hold, so PublisherAdminGate
    // doesn't unmount the whole shell into its full-screen "Loading…" (which
    // scrolled the admin to the top and wiped the form's saved indicator).
    // Only a first load or a genuine station switch shows loading.
    if (!configRef.current || configRef.current.slug !== slug) setLoading(true);
    fetchPublisherConfig(slug).then((c) => {
      if (cancelled) return;
      setConfig(c);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug, version]);

  return { config, loading };
}

export function useAllPublisherConfigs() {
  const [configs, setConfigs] = useState<PublisherConfig[]>(isFresh(allCache) ? allCache.value : []);
  const [loading, setLoading] = useState<boolean>(!isFresh(allCache));
  const [version, setVersion] = useState(cacheVersion);
  useEffect(() => {
    versionListeners.add(setVersion);
    return () => { versionListeners.delete(setVersion); };
  }, []);

  useEffect(() => {
    if (isFresh(allCache)) { setConfigs(allCache.value); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetchAllPublisherConfigs().then((list) => {
      if (cancelled) return;
      setConfigs(list);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [version]);

  return { configs, loading };
}
